/*
<purpose>
  Active-ticket reviewer: two server-side paginated buckets (Offen + Genehmigt),
  tab-switched with shared filters and a sort dropdown + direction flip. Renders rows inline
  (server-filtered by useActivePagination — no client-side re-filter).
</purpose>
<dependencies>
  useActivePagination (server-side cursor paginated rows + counts for each bucket);
  useTicketManager (tm.* mutators for API calls + undo orchestration);
  smarttix.getTicket / getPiiIgnoreList / bulkArchive / bulkUnarchive (direct API);
  TicketDetailComponent, ReanalysisModal, shared components.
</dependencies>
<constraints>
  - Both hooks (offenHook + genehmigtHook) run concurrently; only the active tab's
    sentinel is mounted (avoids double-fetching the invisible bucket on scroll).
  - hook's local removeRow/patchRow give optimistic UI update without a full refetch.
  - After bulk auto-archive: hook.reset() reloads the paginated view.
  - Filters are shared across both buckets so switching tabs keeps context.
  - Per-row unread dot on Offen rows: DB-backed via useReadState (#6). Genehmigt rows
    never show the dot. Opening a row calls markRead() optimistically.
</constraints>
*/
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import TicketDetailComponent from './TicketDetail';
import ReanalysisModal from './ReanalysisModal';
import TicketDetailStage from './TicketDetailStage';
import { openTicketPopout } from './PopoutShell';
import { BentoCard, SelectionToggleIcon, AutoIcon, ArchiveBoxIcon, SpreadsheetRowIcon, DownloadArrowIcon, MassDownloadIcon, FilterPanel, CrossIcon, SortDirectionIcon, TicketRowMeta, ExpandIcon, PopoutIcon, HeaderIconButton } from './shared';
import { onTicketMutated, announceTicketMutated } from '../services/ticketSync';
import { AutoButton } from './AutoButton';
import { FirstRunTooltip } from './FirstRunTooltip';
import { InfoTooltip } from './InfoTooltip';
import { SelectionStrip } from './SelectionStrip';
import { RueckgaengigToast } from './RueckgaengigToast';
import { CountUpNumber } from './CountUpNumber';
import { useSelectionMode } from '../hooks/useSelectionMode';
import { useActivePagination, ActiveFilters, ActiveSort, ActiveDir } from '../hooks/useActivePagination';
import { theme } from '../theme';
import { STATUS_GROUPS, SECONDARY_AXIS_MAX_VISIBLE, POLLING_INTERVAL_MS } from '../constants';
import { smarttix, UserRoles, UiWarps } from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { describeExcelBatchAction } from '../utils/excelBatchAction';
import { TicketDetail, ReanalysisRequest, PiiIgnoreEntry } from '../types';
import { PageType } from './NavBar';
import { useTicketManager } from '../contexts/TicketManagerContext';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { listDetailFlex } from '../services/listDetailRatio';
import { useListDetailFlip } from '../context/ListDetailFlipContext';
import { DateRangeValue } from './DateRangeInput';
import { useReadState } from '../hooks/useReadState';
import { useTicketDetailRefresh, TRANSIENT_STATUSES } from '../hooks/useTicketDetailRefresh';

interface AntwortenSeiteProps {
  onNavigate: (page: PageType, params?: Record<string, string>) => void;
  initialTicket?: string | null;
  userRoles?: UserRoles | null;
  uiWarps: UiWarps;
  /** Days-since-{receivedAt|approvedAt} threshold for Auto-archive.
      0 = disabled; >0 = Auto button archives stale tickets. */
  autoArchiveDays: number;
  /** Demo-only: gates the "Simulierte Systemdaten" fill button in the scaffold editor. */
  simulateSystemData: boolean;
  // Tenant Eingangsweg visibility, threaded from App's single /config read (see TicketDetail).
  showSourceChannel?: boolean;
  // Tenant-wide form-to-Excel capability, threaded from App's single /config read. Gates whether
  // the toggle-into-excel-mode button renders at all — distinct from `counts.formExtractionEnabled`
  // below, which only explains a refusal once selection mode is already entered.
  formExtractionEnabled?: boolean;
  // Tenant-wide inline-image counting rule, threaded from App's single /config read — a per-host
  // config read is forbidden, so this arrives as a prop like showSourceChannel above.
  attachmentsCountInline?: boolean;
}

const daysSince = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
};

const COMPACT_BREAKPOINT = 900;
const MORPH_TRANSITION = 'flex 0.45s cubic-bezier(0.4,0,0.2,1), box-shadow 180ms ease-out, transform 180ms ease-out';

type ActiveTab = 'offen' | 'genehmigt';

const AntwortenSeite: React.FC<AntwortenSeiteProps> = ({
  onNavigate, initialTicket, userRoles, uiWarps, autoArchiveDays, simulateSystemData, showSourceChannel,
  formExtractionEnabled, attachmentsCountInline,
}) => {
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const { isRead, markRead } = useReadState();

  // ─── Selection / UI state ────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>('offen');
  const sel = useSelectionMode();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const pendingUuidRef = useRef<string | null>(initialTicket || null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReanalysisModal, setShowReanalysisModal] = useState(false);
  const [piiIgnoreList, setPiiIgnoreList] = useState<PiiIgnoreEntry[]>([]);
  const [arrivalUuid, setArrivalUuid] = useState<string | null>(null);
  const [removingUuids, setRemovingUuids] = useState<Set<string>>(new Set());
  const [undoToast, setUndoToast] = useState<{ count: number; verb: string; uuids: string[] } | null>(null);
  const [infoToast, setInfoToast] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  // Tab change exits selection (selection is tab-scoped).
  useEffect(() => { if (sel.mode) sel.exitMode(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Detail Stage (maximize/popout) ─────────────────
  const [detailMaximized, setDetailMaximized] = useState(false);
  // Selection change (incl. the stale-selection guard clearing it) always drops the overlay.
  useEffect(() => { setDetailMaximized(false); }, [selectedUuid]);
  const restoreStage = useCallback(() => setDetailMaximized(false), []);
  const popoutFromStage = useCallback(() => {
    if (selectedUuid) openTicketPopout(selectedUuid);
    setDetailMaximized(false); // handoff: the window owns the ticket now
  }, [selectedUuid]);

  // ─── Filters (shared across both buckets) ───────────
  // Newest upload first, for EVERY role — the same default the server resolves when no sort is sent,
  // and the same one UploadsSeite carries, so a clerk moving between the two lists sees one ordering.
  // ⚠ EVIDENCE, NOT PREFERENCE: the previous zone-first default was measured unintuitive in E2E
  // sessions with the client. Read that before "restoring" a hazard-first or activity-first default.
  // ⚠ AND IT IS ROLE-INDEPENDENT BY RULE, not by accident: nothing here may key a default off the
  // tenant's role or its classification schema. `zone` remains a lens the user chooses (see the sort
  // select below); a guard enforces the rule for every component
  // (src/__tests__/sortDefaultRoleIndependence.test.ts).
  const [sort, setSort] = useState<ActiveSort>('uploaded');
  const [dir, setDir] = useState<ActiveDir>('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(id);
  }, [search]);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [reasonCodeFilter, setReasonCodeFilter] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });

  const filters: ActiveFilters = useMemo(() => ({
    search: debouncedSearch,
    primaryAxis: categoryFilter,
    secondaryAxis: reasonCodeFilter,
    from: dateRange.from,
    to: dateRange.to,
    uploadedBy: filterUser,
  }), [debouncedSearch, categoryFilter, reasonCodeFilter, dateRange.from, dateRange.to, filterUser]);

  const hasActiveFilters = !!categoryFilter || reasonCodeFilter.length > 0 || !!search || !!filterUser || !!dateRange.from || !!dateRange.to;

  // ─── Paginated hooks (one per bucket) ───────────────
  const offenHook = useActivePagination('offen', sort, filters, dir);
  const genehmigtHook = useActivePagination('genehmigt', sort, filters, dir);

  const offenRows = offenHook.rows;
  const genehmigtRows = genehmigtHook.rows;
  const activeHook = activeTab === 'offen' ? offenHook : genehmigtHook;
  // ⚠ THE ACTIVE HOOK'S counts, not offenHook's. The old comment here said "counts come from either
  // hook — they carry the same server-side counts object", and that is FALSE as written: the payloads
  // agree only when both fetches SUCCEED. Measured by the pre-merge reviewer: with the offen fetch
  // rejected and the genehmigt fetch holding 5 form-bearing tickets, reading offenHook told the clerk
  // on the Genehmigt tab that their view contained none. Reading the hook whose bucket is on screen
  // keeps the counts, the loading flag and the error from three different sources in agreement.
  const counts = activeHook.counts;
  const activeRows = activeTab === 'offen' ? offenRows : genehmigtRows;

  // ─── Responsive ─────────────────────────────────────
  const [isCompact, setIsCompact] = useState(window.innerWidth < COMPACT_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ─── PII ignore list ────────────────────────────────
  useEffect(() => {
    smarttix.getPiiIgnoreList().then(setPiiIgnoreList);
  }, []);

  // ─── Background poll (silent refresh) ───────────────
  // Fixes reanalysis "hang": after patchRow sets status='reanalyzing', the BE
  // finishes in ~20s and the row returns to suggestion_ready — without a poll
  // the row would show reanalyzing forever. refresh() never clears rows or flips loading.
  // Stable refs avoid recreating the interval when hook objects change each render.
  const offenHookRef = useRef(offenHook);
  const genehmigtHookRef = useRef(genehmigtHook);
  offenHookRef.current = offenHook;
  genehmigtHookRef.current = genehmigtHook;
  useEffect(() => {
    const id = setInterval(() => {
      offenHookRef.current.refresh();
      genehmigtHookRef.current.refresh();
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Foreign-window mutations: silent list refresh + detail refetch when it's ours.
  const selectedUuidRef = useRef(selectedUuid);
  selectedUuidRef.current = selectedUuid;
  useEffect(() => onTicketMutated(async (uuid) => {
    offenHookRef.current.refresh();
    genehmigtHookRef.current.refresh();
    const sel = selectedUuidRef.current;
    if (sel && (uuid === sel || uuid === '*')) {
      const detail = await smarttix.getTicket(sel);
      // Guard: selection may have moved on to a different ticket while this
      // getTicket() was in flight — don't clobber the NOW-selected ticket's
      // detail with a stale response for the ticket the user left behind.
      if (selectedUuidRef.current === sel && detail) setSelectedTicket(detail);
    }
  }), []);

  const handlePiiIgnore = async (fieldType: string, value: string) => {
    await smarttix.addPiiIgnore(fieldType, value);
    setPiiIgnoreList(prev => [...prev, { id: 0, fieldType, value }]);
  };

  // ─── Detail loading ──────────────────────────────────
  useEffect(() => {
    if (!selectedUuid) { setSelectedTicket(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    smarttix.getTicket(selectedUuid).then(detail => {
      if (!cancelled) { setSelectedTicket(detail); setDetailLoading(false); }
    });
    return () => { cancelled = true; };
  }, [selectedUuid]);

  // ─── Transient-status detail refresh ────────────────
  useTicketDetailRefresh(selectedTicket, setSelectedTicket);

  // ─── URL-param ticket resolution ─────────────────────
  // Tries loaded rows first; if not there yet, waits for rows to update.
  useEffect(() => {
    if (!pendingUuidRef.current) return;
    const uuid = pendingUuidRef.current;
    const inRows = [...offenRows, ...genehmigtRows].find(t => t.uuid === uuid);
    if (inRows) {
      pendingUuidRef.current = null;
      setSelectedUuid(uuid);
      setArrivalUuid(uuid);
      setTimeout(() => setArrivalUuid(null), 1600);
    }
    // else: rows not loaded yet; effect re-runs when rows change
  }, [offenRows, genehmigtRows]);

  // ─── Clear stale selection ───────────────────────────
  // Guard: don't clear while detail pane tracks the ticket through a transient status.
  // selectedTicket (kept fresh by useTicketDetailRefresh) is the canonical liveness source.
  // Genuine orphans (non-transient status, or uuid mismatch) still clear immediately.
  useEffect(() => {
    if (selectedUuid && !offenHook.loading && !genehmigtHook.loading) {
      const stillInRows = [...offenRows, ...genehmigtRows].find(t => t.uuid === selectedUuid);
      const detailAlive = selectedTicket?.uuid === selectedUuid && TRANSIENT_STATUSES.has(selectedTicket.status);
      if (!stillInRows && !detailAlive) setSelectedUuid(null);
    }
  }, [offenRows, genehmigtRows, selectedUuid, offenHook.loading, genehmigtHook.loading, selectedTicket]);

  // ─── Actions ─────────────────────────────────────────

  const handleApprove = async () => {
    if (!selectedUuid) return;
    setError(null);
    setActionLoading(true);
    const approvedUuid = selectedUuid;
    const result = await tm.approveTicket(approvedUuid);
    setActionLoading(false);
    if (result.success) {
      offenHook.removeRow(approvedUuid);
      // Why: patchRow only touches a row already present in the hook's OWN array — the
      // just-approved ticket has never been fetched into genehmigtHook, so patching it there
      // used to be a silent no-op and the ticket only surfaced on the next background poll
      // tick (up to POLLING_INTERVAL_MS later). refresh() is the same silent immediate
      // re-fetch the foreign-mutation listener above already runs for every OTHER window's
      // mutations; running it here for OUR OWN mutation closes the identical gap instead of
      // leaving it to the poll. offenHook.refresh() likewise corrects counts.offen (removeRow
      // only ever touched the local rows array, never the server-sourced count) immediately.
      genehmigtHook.refresh();
      offenHook.refresh();
      if (uiWarps.approveToGenehmigt) {
        // Switch to Genehmigt tab so user sees the approved ticket in context.
        // Clear selection first: ticket is no longer in Offen and may not yet be
        // in the loaded Genehmigt page, so a stale detail on the wrong tab is worse
        // than the empty-detail placeholder.
        setSelectedUuid(null);
        setSelectedTicket(null);
        setActiveTab('genehmigt');
      } else {
        const detail = await smarttix.getTicket(approvedUuid);
        setSelectedTicket(detail);
      }
    } else {
      setError(result.error || 'Fehler beim Genehmigen');
    }
  };

  // Copy-approval: ticket stays in Offen, only copyApprovedAt is set.
  const handleCopyApprove = async () => {
    if (!selectedUuid) return;
    const uuid = selectedUuid;
    const result = await tm.copyApproveTicket(uuid);
    if (result.success) {
      offenHook.patchRow(uuid, { copyApprovedAt: new Date().toISOString() });
      setCopyToast(uuid);
    } else {
      setError(result.error || 'Fehler beim Kopieren');
    }
  };

  const handleReanalysis = async (request: ReanalysisRequest) => {
    if (!selectedUuid) return;
    setError(null);
    setActionLoading(true);
    const result = await tm.requestReanalysis(selectedUuid, request);
    setActionLoading(false);
    if (result.success) {
      offenHook.patchRow(selectedUuid, { status: 'reanalyzing' });
      setShowReanalysisModal(false);
      const detail = await smarttix.getTicket(selectedUuid);
      setSelectedTicket(detail);
    } else {
      setError(result.error || 'Fehler beim Ablehnen');
    }
  };

  const handleDownload = async () => {
    if (!selectedUuid) return;
    try {
      await smarttix.downloadDraft(selectedUuid);
    } catch (err: any) {
      setError(formatBackendError(err, 'Herunterladen des Entwurfs'));
    }
  };

  // ─── Auto-archive ─────────────────────────────────────
  // Eligibility over *loaded* rows (same as ArchivSeite: operates on paginated set).
  // Offen ages from receivedAt (visible column). Genehmigt from approvedAt (falls back).
  const offenEligibleUuids = autoArchiveDays > 0
    ? offenRows.filter(t => t.status === 'suggestion_ready' && daysSince(t.receivedAt) >= autoArchiveDays).map(t => t.uuid)
    : [];
  const genehmigtEligibleUuids = autoArchiveDays > 0
    ? genehmigtRows.filter(t => t.status === 'approved' && daysSince(t.approvedAt ?? t.receivedAt) >= autoArchiveDays).map(t => t.uuid)
    : [];

  const eligibleUuids = activeTab === 'offen' ? offenEligibleUuids : genehmigtEligibleUuids;

  const pickedUuids = activeRows.filter(t => sel.selected.has(t.uuid)).map(t => t.uuid);

  // ⚠ THE EMPTY-SELECTION MEANING DIFFERS PER LEVER, AND THAT ASYMMETRY IS DELIBERATE.
  //   archive + nothing picked -> the auto-archive-eligible tickets (a computed, bounded set)
  //   excel   + nothing picked -> EVERY ticket on the active list; the backend keeps only the ones
  //                              carrying a recognised form and names which it kept.
  // A later reader will be tempted to unify these into one "targets" expression. Do not: on the
  // archive list an empty selection means NOTHING (see ArchivSeite), and here it means EVERYTHING.
  // The two lists answer the same gesture differently on purpose.
  // ⚠ "Alle" IS NO LONGER COUNTED HERE. It used to mean "the form-bearing rows this browser happens to
  // hold", which coincides with the bucket only while everything fits one page — so the label said
  // "aller N" in an authoritative voice about a page-sized N. The count is now a server fact over the
  // same bucket+filters the list uses, and the download resolves the same way, so the number the button
  // promises and the rows the workbook contains come from one source instead of two.
  const formBearingCount = counts.formBearing?.[activeTab] ?? 0;
  // Read from the server; never mirrored as a local constant. Infinity when absent so an older backend
  // simply never pre-refuses.
  const formBatchCap = counts.formBatchCap ?? Infinity;
  const formExtractionOff = counts.formExtractionEnabled === false;

  const autoTargetUuids = sel.purpose === 'excel'
    ? pickedUuids                                    // "alle" is a server-side selection now, not a list
    : (pickedUuids.length > 0 ? pickedUuids : eligibleUuids);

  // Would a click do anything, and if not, WHY — one decision, so the tooltip and the refusal toast
  // cannot tell the clerk two different stories. See utils/excelBatchAction.
  const excelAction = describeExcelBatchAction({
    pickedCount: pickedUuids.length,
    formBearingCount,
    formBatchCap,
    formExtractionOff,
    countsLoading: activeHook.loading,
    // A failed fetch is not a measured zero — see the helper's countsFailed branch.
    countsFailed: activeHook.error !== null,
  });
  const excelHasAction = excelAction.canAct;

  const autoHasAction = autoTargetUuids.length > 0;

  // The single action button's FACE is the whole affordance: the lever says which verb, and the icon
  // says which shape of that verb is about to happen. Selecting nothing and selecting everything must
  // not look identical before the click.
  const autoIcon = sel.purpose === 'excel'
    ? (pickedUuids.length > 0
        ? <DownloadArrowIcon size={16} color={theme.colors.white} />
        : <MassDownloadIcon size={16} color={theme.colors.white} />)
    : autoHasAction
      ? <ArchiveBoxIcon size={16} color={theme.colors.white} />
      : <AutoIcon size={16} color={theme.colors.white} />;
  // The Formular-Excel download lives HERE because this is the page a Sachbearbeiter works tickets on.
  // It was first built on the Archiv page, which has the same selection mechanism and was therefore the
  // easy place to put it — and the wrong one: a feature rendered where nobody looks is not delivered.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const handleDownloadXlsx = async () => {
    if (xlsxBusy) return;
    // The reason, not a generic sentence: a clerk blocked by a tenant lever or by the batch cap used to
    // be told "no selection", which is a statement about something they had done nothing wrong with.
    if (!excelHasAction) { setInfoToast(excelAction.refusal!); return; }

    // Two gestures. An explicit pick sends exactly those uuids; an empty selection sends the VIEW, and
    // the server resolves which rows that is — the client no longer enumerates anything for "alle".
    // Built from the SAME `filters` memo the list query uses, not from re-reading the individual filter
    // states: that is what guarantees the download resolves the view the clerk is actually looking at.
    // Rebuilding the filter set by hand here is exactly how the two would drift apart.
    const target = pickedUuids.length > 0
      ? { uuids: pickedUuids }
      : {
          selection: {
            bucket: activeTab,
            sort,
            dir,
            search: filters.search || undefined,
            primaryAxis: filters.primaryAxis || undefined,
            secondaryAxis: filters.secondaryAxis && filters.secondaryAxis.length > 0 ? filters.secondaryAxis : undefined,
            from: filters.from || undefined,
            to: filters.to || undefined,
            uploadedBy: filters.uploadedBy || undefined,
          },
        };

    setXlsxBusy(true);
    try {
      const res = await smarttix.downloadFormRowsXlsx(target);
      if (res.error || !res.blob) { setInfoToast(res.error || 'Excel-Download fehlgeschlagen.'); return; }

      // ⚠ The gap between MATCHED and DELIVERED is the only detector the clerk has, and it catches two
      // different things at once: rows selected whose stored extraction could not be read, and the view
      // having moved between the count on the button and the click. Both were previously invisible.
      const skipped = res.matched - res.delivered;
      if (skipped > 0) {
        setInfoToast(`${res.delivered} von ${res.matched} Vorgaengen konnten uebernommen werden. `
          + `${skipped} enthalten kein lesbares Formular.`);
      }

      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      // The name comes from the client, which composed it from the rows the server actually
      // included. Hardcoding it here once made the rename inert: the api returned the right
      // name and the anchor ignored it.
      a.download = res.fileName ?? 'Notizblatt.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Released on the next tick: revoking synchronously can cancel the download before the browser
      // has read the object.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } finally {
      setXlsxBusy(false);
    }
  };

  const autoNoActionMessage = sel.mode && sel.selected.size === 0
    ? 'Keine Auswahl zum Archivieren.'
    : 'Keine Tickets zum Archivieren.';

  const handleAutoArchive = async () => {
    if (autoTargetUuids.length === 0 || autoSubmitting) return;
    setAutoSubmitting(true);
    const uuids = autoTargetUuids.slice();
    setRemovingUuids(new Set(uuids));
    await new Promise(r => setTimeout(r, 200 + Math.min(40 * uuids.length, 2000)));
    try {
      const res = await smarttix.bulkArchive(uuids);
      if (res.error) {
        setError(res.error);
        setRemovingUuids(new Set());
      } else {
        setUndoToast({ count: res.archived, verb: 'archiviert', uuids });
        announceTicketMutated('*');
        sel.exitMode();
        if (activeTab === 'offen') offenHook.reset();
        else genehmigtHook.reset();
        setRemovingUuids(new Set());
      }
    } finally {
      setAutoSubmitting(false);
    }
  };

  const handleAutoClick = () => {
    if (autoSubmitting) return;
    if (!autoHasAction) { setInfoToast(autoNoActionMessage); return; }
    handleAutoArchive();
  };

  // ONE button, one click handler; the active lever decides the verb. Routing here rather than swapping
  // the handler out keeps the button a single actionable thing, which is the point of the design.
  const handleAutoOrDownload = () => {
    if (sel.purpose === 'excel') { handleDownloadXlsx(); return; }
    handleAutoClick();
  };

  // The tooltip must distinguish "your selection" from "everything on this list" BEFORE the click, and
  // it reads the SAME decision the click will make — the states used to be spelled out twice, and the
  // copy that drifted was the one the clerk only sees after being refused.
  const autoTitle = sel.purpose === 'excel'
    ? excelAction.title
    : (autoHasAction ? `Archivieren (${autoTargetUuids.length})` : autoNoActionMessage);

  const handleUndo = async () => {
    if (!undoToast) return;
    const toastUuids = undoToast.uuids;
    setUndoToast(null);
    await smarttix.bulkUnarchive(toastUuids);
    announceTicketMutated('*');
    offenHook.reset();
    genehmigtHook.reset();
  };

  // ─── Layout ───────────────────────────────────────────
  const { flip } = useListDetailFlip();
  // Flip-OFF static split: list −30px / detail +30px (60px total spread); ratio object untouched so flip-ON 2:1→1:2 morph is unaffected.
  // Flip-ON: 2:1→1:2 morph driven by selection (same behaviour as ArchivSeite).
  const ratio = flip ? listDetailFlex(selectedUuid) : { list: 1, detail: 1 };
  // Flip-off basis offsets: applied only in non-compact non-flip layout.
  const defaultBasis = flip ? {} : { list: 'calc(50% - 30px)', detail: 'calc(50% + 30px)' };
  const listRef = useRef<HTMLDivElement>(null);
  const closeDetail = () => {
    const row = selectedUuid ? listRef.current?.querySelector<HTMLElement>(`[data-uuid="${selectedUuid}"]`) : null;
    setSelectedUuid(null);
    (row ?? listRef.current)?.focus();
  };

  const distinctUploaders = useMemo(() => {
    const emails = new Set([...offenRows, ...genehmigtRows].map(t => t.uploadedByEmail).filter(Boolean) as string[]);
    return Array.from(emails).sort();
  }, [offenRows, genehmigtRows]);

  // ─── Render ───────────────────────────────────────────

  const renderRow = (t: typeof offenRows[0], idx: number, isOffenTab: boolean) => {
    const isSelected = selectedUuid === t.uuid;
    const isPicked = sel.selected.has(t.uuid);
    const isRemoving = removingUuids.has(t.uuid);
    const isArriving = t.uuid === arrivalUuid;
    const showUnreadDot = isOffenTab && !isRead(t.uuid);
    const stripColor = t.status === 'pipeline_error'
      ? theme.statusStrip.error
      : t.primaryAxis ? cm.color(t.primaryAxis) : theme.statusStrip.unclassified;

    const dateIso = activeTab === 'genehmigt' && t.approvedAt ? t.approvedAt : t.receivedAt;

    return (
      <div
        key={t.uuid}
        data-uuid={t.uuid}
        className={isRemoving ? 'stx-archiving' : undefined}
        tabIndex={0}
        aria-label={`Ticket ${t.subject ?? t.uuid}`}
        onClick={() => {
          if (sel.mode) { sel.toggle(t.uuid); return; }
          if (isOffenTab) markRead(t.uuid);
          setSelectedUuid(isSelected ? null : t.uuid);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (sel.mode) { sel.toggle(t.uuid); return; }
            if (isOffenTab) markRead(t.uuid);
            setSelectedUuid(isSelected ? null : t.uuid);
          }
        }}
        style={{
          display: 'flex',
          cursor: 'pointer',
          fontFamily: theme.font.family,
          backgroundColor: isPicked ? theme.colors.bgLight : (isSelected ? theme.colors.bgLight : 'transparent'),
          borderLeft: isPicked ? `4px solid ${theme.colors.navy}` : '4px solid transparent',
          borderBottom: `1px solid ${theme.colors.borderLight}`,
          transition: 'background-color 0.15s ease',
          ...(isArriving ? { animation: 'stx-arrivalPulse 1.5s ease-out' } : {}),
          ...(isRemoving ? { transitionDelay: `${idx * 40}ms` } : {}),
        }}
      >
        {/* Category color strip — no title= tooltip at all when there's no category yet (nothing
            to name); the styled InfoTooltip only wraps the strip once t.primaryAxis is set. */}
        {t.primaryAxis ? (
          <InfoTooltip text={cm.label(t.primaryAxis)}>
            <div style={{
              width: 32, flexShrink: 0,
              backgroundColor: stripColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {cm.rowLayout === 'strip_abbr' && (
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, color: '#fff' }}>
                  {cm.abbr(t.primaryAxis)}
                </span>
              )}
            </div>
          </InfoTooltip>
        ) : (
          <div style={{
            width: 32, flexShrink: 0,
            backgroundColor: stripColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} />
        )}

        {/* Content */}
        <div style={{
          flex: 1, minWidth: 0, position: 'relative',
          // Why: right-padding pre-allocates dot space on Offen rows to prevent layout
          // shift when the dot appears/disappears (UI Layout Stability rule).
          padding: `6px ${theme.spacing.sm + 2}px 6px 12px`,
        }}>
          {/* Unread dot — Offen rows only; DB-backed via useReadState (#6).
              Space pre-allocated via paddingRight; dot fades in/out (Layout Stability). */}
          {showUnreadDot && (
            <div
              data-testid="unread-dot"
              aria-hidden="true"
              style={{
                position: 'absolute', top: theme.spacing.sm, right: theme.spacing.sm,
                width: 8, height: 8, borderRadius: '50%',
                backgroundColor: theme.colors.navy,
                animation: 'stx-fadeIn 0.2s ease',
              }}
            />
          )}
          {/* Row 1: Subject */}
          <div style={{
            fontSize: 13, fontWeight: 500, color: theme.colors.navy,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            paddingRight: isOffenTab ? 16 : 0,
          }}>
            {t.subject || '(Kein Betreff)'}
          </div>

          {/* Rows 2+3: shared meta — category(strip_label)·Anliegen·+N·Anhang // DD.MM.YY·↻N·Kopiert·bearbeitet·Eskalation */}
          <TicketRowMeta ticket={t} dateIso={dateIso} maxSecondaryAxisVisible={SECONDARY_AXIS_MAX_VISIBLE.responses} attachmentsCountInline={attachmentsCountInline} />
        </div>
      </div>
    );
  };

  const offenCount = counts.offen;
  const genehmigtCount = counts.genehmigt;

  return (
    <div style={{ fontFamily: theme.font.family }}>
      {/* Title row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.md,
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        marginBottom: theme.spacing.lg,
        flexWrap: 'wrap',
      }}>
        <h1 style={{
          fontSize: 22, fontWeight: 600, color: theme.colors.navy,
          margin: 0, flex: 1, minWidth: 200,
        }}>
          {activeHook.loading && activeRows.length === 0
            ? 'Antworten'
            : (() => {
                const n = activeTab === 'offen' ? offenCount : genehmigtCount;
                if (n > 0) return `${n} Ticket${n !== 1 ? 's' : ''} ${activeTab === 'offen' ? 'zur Prüfung' : 'genehmigt'}`;
                return 'Antworten';
              })()}
        </h1>
        <SelectionStrip count={sel.selected.size} onClear={sel.clear} />
        {activeRows.length > 0 && (
          <button
            onClick={() => sel.setPurpose('archive')}
            aria-label={sel.purpose === 'archive' ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'}
            title={sel.purpose === 'archive' ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0,
              background: sel.purpose === 'archive' ? theme.colors.navy : 'transparent',
              border: `1px solid ${sel.purpose === 'archive' ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              cursor: 'pointer',
              color: sel.purpose === 'archive' ? theme.colors.white : theme.colors.navyMid,
            }}
          >
            <SelectionToggleIcon size={14} color={sel.purpose === 'archive' ? theme.colors.white : theme.colors.navyMid} active={sel.purpose === 'archive'} />
          </button>
        )}

        {/* The form-to-excel LEVER. Same affordance as the multi-select beside it — inactive is navy on
            white, active flips to white on navy — because it does the same thing: it enters selection
            mode. The only difference is which verb the single action button then carries, so the two
            must look like siblings rather than like a toggle and a command.
            Gated on the tenant capability FIRST: a role that does not own this must never see the
            affordance exists, not merely find it refuses on click. No reserve/placeholder while
            config is unresolved — the prop defaults false, so this simply stays absent until it
            resolves true, same as every other config-gated control on this page. */}
        {formExtractionEnabled && activeRows.length > 0 && (
          <button
            onClick={() => sel.setPurpose('excel')}
            aria-label={sel.purpose === 'excel' ? 'Formular-Excel-Auswahl beenden' : 'Formularzeilen als Excel auswaehlen'}
            title={sel.purpose === 'excel' ? 'Formular-Excel-Auswahl beenden' : 'Formularzeilen als Excel auswaehlen'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0,
              background: sel.purpose === 'excel' ? theme.colors.navy : 'transparent',
              border: `1px solid ${sel.purpose === 'excel' ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              cursor: 'pointer',
              color: sel.purpose === 'excel' ? theme.colors.white : theme.colors.navyMid,
            }}
          >
            <SpreadsheetRowIcon size={14} color={sel.purpose === 'excel' ? theme.colors.white : theme.colors.navyMid} />
          </button>
        )}
        <FirstRunTooltip
          storageKey="auto-button-v1"
          title="Auto-Aktion"
          body="Ein Klick führt die Aktion aus, die zur aktiven Auswahl gehört: ohne Auswahl-Modus das Archivieren, im Formular-Excel-Modus den Download der Formularzeilen."
        >
          <AutoButton
            icon={autoIcon}
            onClick={handleAutoOrDownload}
            title={autoTitle}
          />
        </FirstRunTooltip>
      </div>

      {/* Main layout: list + detail */}
      <div style={{
        display: 'flex', gap: theme.spacing.lg,
        flexDirection: isCompact ? 'column' : 'row',
        ...(isCompact ? {} : { height: 'calc(100vh - 200px)', minHeight: 400 }),
      }}>
        {/* Left: ticket list */}
        <BentoCard
          title="Tickets"
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            flex: isCompact ? 1 : ratio.list,
            ...(isCompact ? { maxHeight: 500 } : {
              flexBasis: defaultBasis.list,
              transition: MORPH_TRANSITION,
            }),
          }}
          contentStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, padding: 0 }}
        >
          <FilterPanel
            search={search}
            onSearchChange={setSearch}
            open={filterOpen}
            onOpenChange={setFilterOpen}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            reasonCodeFilter={reasonCodeFilter}
            onReasonCodeFilterChange={setReasonCodeFilter}
            uploaders={distinctUploaders}
            filterUser={filterUser}
            onFilterUserChange={setFilterUser}
            from={dateRange.from}
            to={dateRange.to}
            onDateRangeChange={setDateRange}
            onClearAll={() => {
              setSearch(''); setCategoryFilter(null);
              setReasonCodeFilter([]); setFilterUser(''); setDateRange({ from: null, to: null });
            }}
            hasActiveFilters={hasActiveFilters}
          />

          {/* Tabs: Offen / Genehmigt */}
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            marginBottom: theme.spacing.sm,
            userSelect: 'none',
          }}>
            {([
              { key: 'offen' as const, label: 'Offen', count: offenCount },
              { key: 'genehmigt' as const, label: 'Genehmigt', count: genehmigtCount },
            ]).map(({ key, label, count }) => {
              const active = activeTab === key;
              return (
                <button key={key} onClick={() => setActiveTab(key)} title={label} style={{
                  flex: 1, minWidth: 0,
                  display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4,
                  padding: '8px 0', background: 'none',
                  border: 'none', borderBottom: `2px solid ${active ? theme.colors.navy : 'transparent'}`,
                  fontSize: 13, fontWeight: active ? 600 : 400,
                  color: active ? theme.colors.navy : theme.colors.textSecondary,
                  cursor: 'pointer', fontFamily: theme.font.family,
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{label}</span>
                  <span style={{ flexShrink: 0 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* Sort field dropdown + direction flip */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs}px ${4 + theme.spacing.sm}px`,
            fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            userSelect: 'none', gap: 4,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>Betreff / {cm.categoryNoun}</div>
            <select
              id="antworten-sort"
              name="antworten-sort"
              value={sort}
              onChange={e => setSort(e.target.value as ActiveSort)}
              aria-label="Sortierspalte"
              style={{
                flexShrink: 0, fontSize: 11, fontWeight: 600,
                color: theme.colors.navy, background: theme.colors.white,
                border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.sm,
                padding: '2px 4px', cursor: 'pointer',
                fontFamily: theme.font.family,
                outline: 'none', textTransform: 'uppercase', letterSpacing: 0.3,
              }}
            >
              {cm.schemaActive && <option value="zone">Zone</option>}
              <option value="updated">Aktualisiert</option>
              <option value="uploaded">Hochgeladen</option>
              <option value="received">E-Mail-Eingang</option>
              <option value="rejections">Neuanalysen</option>
            </select>
            <button
              onClick={() => setDir(d => d === 'desc' ? 'asc' : 'desc')}
              aria-label={dir === 'desc' ? 'Absteigende Reihenfolge — klicken für aufsteigend' : 'Aufsteigende Reihenfolge — klicken für absteigend'}
              title={dir === 'desc' ? 'Absteigende Reihenfolge' : 'Aufsteigende Reihenfolge'}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, padding: 0,
                color: theme.colors.navy, background: 'none',
                border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.sm,
                cursor: 'pointer',
              }}
            >
              <SortDirectionIcon size={13} color={theme.colors.navy} dir={dir} />
            </button>
          </div>

          {/* Row list */}
          <div ref={listRef} tabIndex={-1} className="stx-no-scrollbar" style={{ flex: 1, overflowY: 'auto', outline: 'none' }}>
            {activeHook.loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                Lade Tickets...
              </div>
            ) : activeHook.error ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.error, fontSize: 13 }}>
                {activeHook.error}
                <div style={{ marginTop: theme.spacing.sm }}>
                  <button
                    onClick={() => activeHook.reset()}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      fontFamily: theme.font.family,
                      background: 'transparent', color: theme.colors.navy,
                      border: `1px solid ${theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                    }}
                  >Erneut versuchen</button>
                </div>
              </div>
            ) : activeRows.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                {hasActiveFilters ? 'Keine Treffer' : 'Keine Tickets vorhanden'}
              </div>
            ) : (
              activeRows.map((t, idx) => renderRow(t, idx, activeTab === 'offen'))
            )}

            {/* Infinite-scroll sentinel — only mounted for active tab */}
            {!activeHook.loading && !activeHook.error && activeRows.length > 0 && (
              <div ref={activeHook.sentinelRef} aria-hidden="true" style={{ height: 1 }} />
            )}
            {activeHook.loadingMore && (
              <div style={{ padding: 16, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                Lade weitere…
              </div>
            )}
          </div>
        </BentoCard>

        {/* Right: ticket detail */}
        <BentoCard
          title="Details"
          headerAction={selectedUuid ? (
            <div style={{ display: 'flex', gap: 4, animation: 'stx-fadeIn 0.2s ease' }}>
              <HeaderIconButton label="Maximieren" onClick={() => setDetailMaximized(true)}><ExpandIcon size={15} /></HeaderIconButton>
              <HeaderIconButton label="In eigenem Fenster öffnen" onClick={popoutFromStage}><PopoutIcon size={15} /></HeaderIconButton>
              <HeaderIconButton label="Auswahl aufheben" onClick={closeDetail}><CrossIcon size={16} /></HeaderIconButton>
            </div>
          ) : undefined}
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            ...(isCompact
              ? { width: '100%', flexShrink: 0, minHeight: 300 }
              : { flex: ratio.detail, flexBasis: defaultBasis.detail, transition: MORPH_TRANSITION }),
          }}
          contentStyle={{ padding: theme.spacing.lg, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}
        >
          {!selectedUuid ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 15,
            }}>
              Ticket auswählen um Details anzuzeigen
            </div>
          ) : !selectedTicket && !detailLoading ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 15,
            }}>
              Ticket nicht gefunden
            </div>
          ) : selectedTicket ? (
            <TicketDetailStage
              maximized={detailMaximized}
              onRestore={restoreStage}
              onPopout={popoutFromStage}
              title={selectedTicket.subject || '(Kein Betreff)'}
            >
              <TicketDetailComponent
              onInfo={setInfoToast}
                ticket={selectedTicket}
                loading={detailLoading}
                actionLoading={actionLoading}
                error={error}
                onClearError={() => setError(null)}
                onApprove={handleApprove}
                onCopyApprove={handleCopyApprove}
                onReject={() => setShowReanalysisModal(true)}
                onDownload={handleDownload}
                onGoToUploads={STATUS_GROUPS.eingereicht.includes(selectedTicket.status) && !tm.processingUuids.has(selectedTicket.uuid) ? () => onNavigate('uploads', { ticket: selectedTicket.uuid }) : undefined}
                onReprocess={selectedTicket.status === 'pipeline_error' ? async () => {
                  setActionLoading(true);
                  setError(null);
                  const result = await tm.processTicket(selectedTicket.uuid);
                  setActionLoading(false);
                  if (result.success) {
                    offenHook.patchRow(selectedTicket.uuid, { status: 'reanalyzing' });
                    const detail = await smarttix.getTicket(selectedTicket.uuid);
                    setSelectedTicket(detail);
                  } else {
                    setError(result.error || 'Fehler beim erneuten Verarbeiten');
                  }
                } : undefined}
                onArchive={['approved', 'sent', 'pipeline_error'].includes(selectedTicket.status) ? async () => {
                  setActionLoading(true);
                  setError(null);
                  const result = await tm.archiveTicket(selectedTicket.uuid);
                  setActionLoading(false);
                  if (result.success) {
                    offenHook.removeRow(selectedTicket.uuid);
                    genehmigtHook.removeRow(selectedTicket.uuid);
                    setSelectedUuid(null);
                    setSelectedTicket(null);
                  } else {
                    setError(result.error || 'Fehler beim Archivieren');
                  }
                } : undefined}
                piiIgnoreList={piiIgnoreList}
                onPiiIgnore={handlePiiIgnore}
                onSuggestionEdited={async () => {
                  const detail = await smarttix.getTicket(selectedTicket.uuid);
                  setSelectedTicket(detail);
                }}
                simulateSystemData={simulateSystemData}
                showSourceChannel={showSourceChannel}
                attachmentsCountInline={attachmentsCountInline}
              />
            </TicketDetailStage>
          ) : (
            <TicketDetailComponent
              ticket={null as any}
              loading={true}
              actionLoading={false}
              error={null}
              onClearError={() => {}}
              onApprove={() => {}}
              onReject={() => {}}
              onDownload={() => {}}
            />
          )}
        </BentoCard>
      </div>

      <RueckgaengigToast
        visible={!!undoToast}
        message={undoToast ? (
          <><CountUpNumber target={undoToast.count} /> Ticket{undoToast.count !== 1 ? 's' : ''} {undoToast.verb}</>
        ) : ''}
        onUndo={handleUndo}
        onDismiss={() => setUndoToast(null)}
      />

      <RueckgaengigToast
        visible={!!infoToast}
        message={infoToast ?? ''}
        onDismiss={() => setInfoToast(null)}
      />

      <RueckgaengigToast
        visible={!!copyToast}
        message="Vorschlag kopiert — zählt als genehmigt"
        durationMs={8000}
        onUndo={() => {
          if (copyToast) {
            tm.undoCopyApprove(copyToast);
            offenHook.patchRow(copyToast, { copyApprovedAt: undefined });
          }
          setCopyToast(null);
        }}
        onDismiss={() => setCopyToast(null)}
      />

      {showReanalysisModal && selectedTicket && (
        <ReanalysisModal
          ticketUuid={selectedTicket.uuid}
          currentCategory={selectedTicket.primaryAxis}
          reanalysisCount={selectedTicket.reanalysisCount}
          reanalysisBlocked={selectedTicket.reanalysisBlocked}
          attachments={selectedTicket.attachments}
          ocrDecisions={selectedTicket.ocrDecisions}
          onSubmit={handleReanalysis}
          onUnblock={async () => {
            setActionLoading(true);
            const result = await tm.unblockReanalysis(selectedTicket.uuid);
            setActionLoading(false);
            if (result.success) {
              offenHook.patchRow(selectedTicket.uuid, { reanalysisBlocked: false });
              const detail = await smarttix.getTicket(selectedTicket.uuid);
              setSelectedTicket(detail);
            } else {
              setError(result.error || 'Fehler beim Entsperren');
            }
          }}
          onClose={() => setShowReanalysisModal(false)}
          isSubmitting={actionLoading}
          isAdmin={!!(userRoles?.isCustomerAdmin || userRoles?.isAdmin)}
          // Same value the detail pane above receives, from the same App-level /config read: the
          // modal opens ON TOP of that pane, so a dropped prop here puts two different attachment
          // counts on screen at the same time for the same ticket.
          attachmentsCountInline={attachmentsCountInline}
        />
      )}
    </div>
  );
};

export default AntwortenSeite;
